import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { submitExamQuiz } from "../../../../../api";
import { Markdown } from "@quark/design";
import {
  ExamMode,
  ExamSingleChoice,
  ExamSingleChoiceOption,
} from "../../../../../types/exam";
import ExamOption from "../common/option";
import styles from "./index.module.scss";

interface ExamSingleChoiceProps {
  dataSource: ExamSingleChoice;
  logId: number;
  index: number;
  mode: ExamMode;
  examSubmitted: boolean;
  onChange: () => void;
  onRight: () => void;
}

const ExamSingleChoiceComp: FC<ExamSingleChoiceProps> = ({
  dataSource,
  logId,
  index,
  mode,
  examSubmitted,
  onChange,
  onRight,
}) => {
  const {
    title,
    question_text,
    question_options: options,
    description,
    question_id,
    user_answers,
  } = dataSource;

  const [answerOptionId, setAnswerOptionId] = useState<number>(0);

  const correctOption = useMemo<ExamSingleChoiceOption | undefined>(() => {
    return options.find((opt) => opt.correct);
  }, [options]);

  const getOptionType = useCallback(
    (opt: ExamSingleChoiceOption) => {
      if (!answerOptionId) return;
      if (opt.id === answerOptionId && !opt.correct) {
        return "error";
      }
      if (opt.correct) {
        return "success";
      }
      return;
    },
    [answerOptionId]
  );

  const optionClick = (clickOption: ExamSingleChoiceOption) => {
    if (answerOptionId) return;
    if (examSubmitted) {
      // toast 试卷已提交
      return;
    }
    setAnswerOptionId(clickOption.id);
    if (logId) {
      submitExamQuiz({
        question_id,
        user_answers: [{ id: clickOption.id }],
        src_key: `exam_log_${logId}`,
      }).then(() => {
        onChange();
      });
    }
    // 答案正确
    if (correctOption?.id === clickOption.id) {
      onRight();
    }
  };

  useEffect(() => {
    if (mode === "recite" || examSubmitted) {
      // 背题模式，设置为正确答案
      setAnswerOptionId(correctOption?.id || 0);
    } else if (mode === "practice") {
      // 答题模式，设置为用户答案或默认答案
      const answerId = user_answers ? user_answers[0].id : 0;
      if (answerId) {
        setAnswerOptionId(answerId);
      } else {
        setAnswerOptionId(0);
      }
    }
  }, [mode, correctOption, user_answers, examSubmitted]);

  return (
    <div className={styles.quiz}>
      <div className={styles.topic}>
        <div className={styles.title}>
          <div className={styles.index}>{index + 1}、</div>
          <div className={styles.question}>{title}</div>
        </div>
        {!!question_text && <Markdown children={question_text} />}
        {!!description && <Markdown children={description} />}
      </div>

      <div className={styles.options}>
        {options.map((option) => (
          <ExamOption
            className={styles.option}
            key={option.id}
            type={getOptionType(option)}
            onClick={() => optionClick(option)}
          >
            <span className={styles.optionKey}>{option.key}</span>
            <Markdown children={option.text} />
          </ExamOption>
        ))}
        {!!answerOptionId && (
          <ExamOption disabled>正确答案: {correctOption?.key}</ExamOption>
        )}
      </div>

      {!!answerOptionId && (
        <div className={styles.explanation}>
          <div className={styles.title}>答案解析</div>
          <div className={styles.content}>
            {/* <div className={styles.key}>{correctOption?.key}</div> */}
            <div className={styles.text}>
              <Markdown children={correctOption?.explanation.md_text || ""} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamSingleChoiceComp;
